import { useEffect, useState } from 'react';

import { ContentState, EditorState, Modifier, RichUtils } from 'draft-js';

import { IEditableTextProps } from './types';

export const useEditableText = ({
  text: rawText,
  onChange,
  onBlur = () => {},
  autofocus,
  isLineBreak,
}: Pick<IEditableTextProps, 'onChange' | 'onBlur' | 'autofocus' | 'isLineBreak' | 'text'>) => {
  const text = rawText ?? '';
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [editorState, setEditorState] = useState<EditorState>(
    EditorState.createWithContent(ContentState.createFromText(text)),
  );

  useEffect(() => {
    if (autofocus) {
      handleFocus();
      setEditorState(EditorState.moveFocusToEnd(editorState));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autofocus]);

  useEffect(() => {
    if (!isFocused && text !== editorState.getCurrentContent().getPlainText()) {
      setEditorState(EditorState.createWithContent(ContentState.createFromText(text)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const handleChange = (newEditorState: EditorState) => {
    const contentText = newEditorState.getCurrentContent().getPlainText();
    const currentContentText = editorState.getCurrentContent().getPlainText();

    if (contentText !== currentContentText) {
      onChange(contentText);
    }

    setEditorState(newEditorState);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    onBlur();
  };

  const handleReturn = () => {
    if (isLineBreak) {
      const selection = editorState.getSelection();
      if (selection.isCollapsed()) {
        setEditorState(RichUtils.insertSoftNewline(editorState));
      } else {
        const content = editorState.getCurrentContent();
        let newContent = Modifier.removeRange(content, selection, 'forward');
        const newSelection = newContent.getSelectionAfter();
        const block = newContent.getBlockForKey(newSelection.getStartKey());
        newContent = Modifier.insertText(
          newContent,
          newSelection,
          '\n',
          block.getInlineStyleAt(newSelection.getStartOffset()),
          /*@ts-ignore*/
          null,
        );

        setEditorState(EditorState.push(editorState, newContent, 'insert-fragment'));
      }

      return 'not-handled';
    }

    return 'handled';
  };

  return {
    editorState,
    isFocused,
    handleChange,
    handleFocus,
    handleBlur,
    handleReturn,
  };
};
